import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { type Capacidad, type Permisos, type Preset, useCatalogoDeCapacidades } from '../api/capacidades'
import { consulta, ErrorDeLaApi, pedir } from '../api/nucleo'
import type { Lista } from '../api/tipos'
import { Aviso, Boton, Esqueleto, Tarjeta } from '../componentes/ui'
import { CasillasDeCapacidades, type Eleccion } from './CasillasDeCapacidades'

/**
 * «Qué puede hacer en esta cuenta», debajo de la ficha de una persona: su fila de
 * `accesos` en el espacio actual (usuario × cliente), con su preset y sus casillas. La
 * edita quien tiene `usuarios.editar` (`PATCH /api/accesos/<id>`); los demás la ven en
 * solo lectura. Las reglas —el propietario no se recorta a sí mismo, solo el propietario
 * nombra a otro, nadie deja la cuenta sin propietario— son del núcleo, y su mensaje se
 * enseña tal cual junto al control (§28).
 */
type Acceso = { id: number; rol: Preset; capacidades?: Capacidad[] | null; sitios?: unknown[] | null }

export const claveDeAcceso = (espacio: number, usuario: number) => ['accesos', espacio, usuario] as const

export const AccesoDelUsuario = ({ espacio, usuario, permisos, yo }: { espacio: number; usuario: number; permisos: Permisos; yo: null | number }) => {
  const clienteDeConsultas = useQueryClient()
  const { data: catalogo } = useCatalogoDeCapacidades()
  const {
    data: acceso,
    isPending,
    error,
  } = useQuery({
    queryKey: claveDeAcceso(espacio, usuario),
    queryFn: async () => (await pedir<Lista<Acceso>>(`/accesos${consulta({ 'where[usuario][equals]': usuario, 'where[cliente][equals]': espacio, depth: 0, limit: 1 })}`)).docs[0] ?? null,
  })
  const [borrador, setBorrador] = useState<Eleccion | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [mensaje, setMensaje] = useState<null | { texto: string; tono: 'error' | 'ok' }>(null)

  const puedeEditar = permisos.tiene('usuarios.editar')
  // Nombrar propietario es del dueño: a quien no lo es no se le ofrece (el núcleo lo rechazaría igual).
  const presetsDisponibles: Preset[] | undefined = permisos.esDueno ? undefined : ['administrador', 'editor', 'autor', 'marketing']

  if (isPending) {
    return (
      <Tarjeta className="mt-6">
        <Esqueleto filas={3} />
      </Tarjeta>
    )
  }
  if (error) return null
  if (!acceso) {
    return (
      <Tarjeta className="mt-6">
        <h2 className="font-display text-base font-semibold">Qué puede hacer en esta cuenta</h2>
        <p className="mt-2 text-sm text-tenue">Esta persona no tiene acceso a este espacio de trabajo. Si pertenece a la cuenta, su acceso aparece solo.</p>
      </Tarjeta>
    )
  }

  const guardado: Eleccion = { preset: acceso.rol, capacidades: acceso.capacidades ?? [] }
  const eleccion = borrador ?? guardado
  const sucio = borrador !== null && (borrador.preset !== guardado.preset || borrador.capacidades.length !== guardado.capacidades.length || borrador.capacidades.some((c) => !guardado.capacidades.includes(c)))

  const guardar = async () => {
    if (!borrador) return
    setGuardando(true)
    setMensaje(null)
    try {
      await pedir(`/accesos/${acceso.id}?depth=0`, { metodo: 'PATCH', cuerpo: { rol: borrador.preset, capacidades: borrador.capacidades } })
      setBorrador(null)
      setMensaje({ texto: 'Guardado. Lo nuevo vale desde su próxima acción.', tono: 'ok' })
      void clienteDeConsultas.invalidateQueries({ queryKey: claveDeAcceso(espacio, usuario) })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'accesos'] })
      // Si me cambio a mí mismo, el menú tiene que enterarse.
      if (usuario === yo) void clienteDeConsultas.invalidateQueries({ queryKey: ['mis-capacidades', espacio] })
    } catch (e) {
      setMensaje({ texto: e instanceof ErrorDeLaApi ? e.message : 'No hemos podido guardar. Vuelve a intentarlo.', tono: 'error' })
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Tarjeta className="mt-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-base font-semibold">Qué puede hacer en esta cuenta</h2>
          <p className="mt-1 text-sm text-tenue">
            {puedeEditar ? 'Un punto de partida y, si hace falta, casilla a casilla. Lo que se cambia aquí vale solo para este espacio de trabajo.' : 'Lo cambia quien administra las personas de la cuenta.'}
          </p>
        </div>
        {puedeEditar && (
          <div className="flex items-center gap-2">
            {sucio && (
              <Boton disabled={guardando} onClick={() => setBorrador(null)} pequeno tipo="discreto">
                Deshacer
              </Boton>
            )}
            <Boton cargando={guardando} disabled={!sucio || guardando} onClick={() => void guardar()} pequeno tipo="primario">
              Guardar permisos
            </Boton>
          </div>
        )}
      </div>
      {mensaje && (
        <div className="mb-4">
          <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>
        </div>
      )}
      {catalogo ? (
        <CasillasDeCapacidades cambiar={setBorrador} catalogo={catalogo} eleccion={eleccion} presetsDisponibles={presetsDisponibles} soloLectura={!puedeEditar} />
      ) : (
        <Esqueleto filas={4} />
      )}
    </Tarjeta>
  )
}
